import { useMemo, type ReactNode } from 'react'

import { TabBar, type TabItem } from '@/components/molecules/TabBar'
import { useRecordingImages } from '@/apps/latest/features/attachments/useRecordingImages'
import { useReleaseChannel } from '@/features/auth'

import type { ViewStatus } from '@/apps/latest/shared/recording-status/types'

export type MeetingTab = 'highlight' | 'transcript' | 'memo' | 'attachments'

const BASE_TABS: TabItem<MeetingTab>[] = [
  { key: 'highlight', label: 'ハイライト' },
  { key: 'transcript', label: '文字起こし' },
  { key: 'memo', label: 'メモ' },
]

// 添付資料 (問診票・お薬手帳・残薬の写真・スキャンの PDF) は β 版機能. 提供先は release_channel=beta 以上の
// ユーザーに限定する (他の β 機能: 情報提供書・在宅 pill・調剤鑑査と同じゲート). ga ユーザーにはタブごと非表示.
// 録る前・録音中の足す口はこのタブで、完了後は SOAP の下の「資料」の節 (design-book 04-apps/kakeru.md「資料添付 (薬剤師)」)。
const ATTACHMENTS_TAB: TabItem<MeetingTab> = {
  key: 'attachments',
  label: '添付資料',
}

// タブの並びの既定 (= 薬剤師の記録ブロック)。呼び出し側が並びを渡さないときはこれ。
// 添付資料は 1 件以上なら件数をタブ名に出す (受付で付けた紙を、録る前の薬剤師がタブを開かずに「入っている」と分かる)。
// 語は「N 件」(裸の「N」は何の数か言わない。他の件数 = 提案 N 件・資料 N 件 と同じ。hub log/108 決定 48)。
export const useDefaultMeetingTabs = (
  yakurekiId: string | null
): TabItem<MeetingTab>[] => {
  const { isBeta } = useReleaseChannel()
  const { list } = useRecordingImages(isBeta ? yakurekiId : null)
  const count = list.data?.length ?? 0
  return useMemo(
    () =>
      isBeta
        ? [
            ...BASE_TABS,
            {
              ...ATTACHMENTS_TAB,
              label: count > 0 ? `添付資料 ${count} 件` : ATTACHMENTS_TAB.label,
            },
          ]
        : BASE_TABS,
    [isBeta, count]
  )
}

// 呼び出し側が並びを決めるときの部品。label は既定と同じものを使う。
export const MEETING_TAB_ITEMS: Record<MeetingTab, TabItem<MeetingTab>> = {
  highlight: BASE_TABS[0],
  transcript: BASE_TABS[1],
  memo: BASE_TABS[2],
  attachments: ATTACHMENTS_TAB,
}

interface Props {
  active: MeetingTab
  onChange: (tab: MeetingTab) => void
  status: ViewStatus
  stream: MediaStream | null
  tabs: TabItem<MeetingTab>[]
  // 右端 (β の録音中の経過時間. β は見出しの行を持たない)
  rightSlot?: ReactNode
}

// tab strip のみ. 録音中の音声振幅は記録ブロック内の波形ではなく、 録音ボタン周りの
// 脈動 ring (RecordingControl / useAudioPulse) で表すため、 ここに波形 right slot は置かない.
export const MeetingTabBar = ({ active, onChange, tabs, rightSlot }: Props) => (
  <TabBar
    tabs={tabs}
    active={active}
    onChange={onChange}
    rightSlot={rightSlot}
  />
)
